'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { TooltipFloatingContext } from './context/tooltip-floating.context';
import { TooltipRootContext } from './context/tooltip-root.context';
import {
  tooltipContentPositionerStyles,
  tooltipContentStyles,
} from './styles/tooltip-content.styles';
import type { TooltipContentPropsInterface } from './types/tooltip-content.types';

/**
 * @since 10.0.0
 */
export const TooltipContent = forwardRef<HTMLDivElement, TooltipContentPropsInterface>(
  (props: TooltipContentPropsInterface, forwardedRef) => {
    const { tooltip } = useContext(TooltipRootContext);
    const floating = useContext(TooltipFloatingContext);
    const {
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const mergedRefs = useMergeRefs([tooltip.refs.setFloating, forwardedRef]);

    const contentPositionerStyles = css(tooltipContentPositionerStyles);
    const contentStyles = css(tooltipContentStyles, cssProp);

    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      disabled: !tooltip.isControlled,
      order: ['floating', 'content'],
      modal: false,
      ...userFocusManagerProps,
    };

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <div
          ref={mergedRefs}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Tooltip.ContentPositioner"
          className={cx('c11n', contentPositionerStyles)}
          style={{
            ...tooltip.floatingStyles,
            visibility: tooltip.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
          }}
          {...floating.getFloatingProps(rest)}
        >
          <div
            data-c11n-version={__C11N_VERSION__}
            data-c11n-component="Tooltip.Content"
            className={cx('c11n', contentStyles, className)}
            style={{ ...tooltip.floatingTransitionStyles, ...style }}
          >
            {children}
          </div>
        </div>
      </FloatingFocusManager>
    );
  },
);

TooltipContent.displayName = 'Tooltip.Content';
